import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import React from 'react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import type { FileRouteTypes } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { FormInput } from '../../../../components/forms/FormInput';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import useChart from '../../../../hooks/useChart';
import { Chart, PatientDoc } from '@usacsmideo/types';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { Button } from '../../../../components/buttons/Button';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { classNames } from '../../../../style/classNames';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';

import 'react-phone-number-input/style.css';

import { FieldInfo } from '../../../../components/FieldInfo';
export const Route = createFileRoute('/_authenticated/$patientId/careTeam/doctorInfo')({
  component: DoctorInfoPage,
});

import * as Sentry from '@sentry/browser';

import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';

function DoctorInfoPage() {
  const { mutate } = useUpdateChartSection();
  const { patientId } = Route.useParams();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentLocation = routerState.location;
  const pathname = currentLocation.pathname;

  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo: patientInfo,
      chart: chart,
    });

    if ('incompleteMessage' in res) {
      // THE PATIENT HAS NOT COMPLETED THE NECESSARY SCREENS
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      // FIRESTORE ERROR
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    // NAVIGATION COMPLETE — PERSIST FINAL STATE AND NAVIGATE HOME
    if ('complete' in res) {
      mutatePatientDoc(
        { patientId: patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/' });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }


    mutatePatientDoc(
      {
        patientId: patientId,
        navObj: res.updatedNavObj,
      },
      {
        onSuccess: () => {
          // NAVIGATE TO THE NEXT SCREEN
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error: Error) => {
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          alert(
            'There was an error updating your information. Please try again later or contact support if this issue persists.',
          );
        },
      },
    );
  };

  const doctorInfoForm = useForm({
    defaultValues: {
      doctor: chart?.DOCTOR_INFO?.doctor ? chart?.DOCTOR_INFO?.doctor : '',
      drStreetAddress: chart?.DOCTOR_INFO?.drStreetAddress ? chart?.DOCTOR_INFO?.drStreetAddress : '',
      drCity: chart?.DOCTOR_INFO?.drCity ? chart?.DOCTOR_INFO?.drCity : '',
      drState: chart?.DOCTOR_INFO?.drState ? chart?.DOCTOR_INFO?.drState : '',
      drZIP: chart?.DOCTOR_INFO?.drZIP ? chart?.DOCTOR_INFO?.drZIP : '',
      drCountry: chart?.DOCTOR_INFO?.drCountry ? chart?.DOCTOR_INFO?.drCountry : '',
    },
    onSubmit: () => {
      handleDrInfoUpdate();
    },
  });

  // WRITE FORM DATA TO FIRESTORE
  const handleDrInfoUpdate = () => {
    mutate(
      {
        patientId: patientId,
        docData: {
          DOCTOR_INFO: {
            doctor: doctorInfoForm.state.values.doctor,
            drStreetAddress: doctorInfoForm.state.values.drStreetAddress,
            drCity: doctorInfoForm.state.values.drCity,
            drState: doctorInfoForm.state.values.drState,
            drZIP: doctorInfoForm.state.values.drZIP,
            drCountry: doctorInfoForm.state.values.drCountry,
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
          setSubmitted(true);
        },
        onError: (error) => {
          Sentry.captureException(error); // SEND ERROR TO SENTRY
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId) {
    return (
      <>
        <div id="photo-upload-page" className="flex flex-col">
          <h1 className="place-self-center text-3xl font-bold">DOCTOR INFORMATION</h1>
          <div className="my-4">
            <div id="front-id-container" className="flex flex-col items-center">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  doctorInfoForm.handleSubmit();
                }}
              >
                <div className="flex flex-col">
                  <doctorInfoForm.Field
                    name="doctor"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length < 5) {
                          return 'Doctor name should be more than five characters';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Doctor's Full Name*"
                          id="doctor-name"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          // required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <doctorInfoForm.Field
                    name="drStreetAddress"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length < 5) {
                          return 'Street address should be more than five characters';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Doctor's Street Address*"
                          id="dr-street-address"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <doctorInfoForm.Field
                    name="drCity"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'City cannot be blank';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Doctor's City*"
                          id="dr-city"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <doctorInfoForm.Field
                    name="drState"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'State cannot be blank';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Doctor's State*"
                          id="dr-state"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <doctorInfoForm.Field
                    name="drZIP"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'ZIP code cannot be blank';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Doctor's ZIP Code*"
                          id="dr-ZIP"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <doctorInfoForm.Field
                    name="drCountry"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'Country cannot be blank';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Doctor's Country*"
                          id="dr-country"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                </div>
                <div className="mt-8 flex flex-col items-center">
                  <doctorInfoForm.Subscribe
                    selector={(state) => [state.canSubmit, state.errors]}
                    children={([canSubmit]) => (
                      <>
                        {!canSubmit && (
                          <div className="mt-4 flex flex-col items-center">
                            <p className="text-red-500">Please correct any errors above before re-submitting.</p>
                          </div>
                        )}
                        <Button
                          id="submit-hca-info"
                          type="submit"
                          disabled={!canSubmit}
                          onClick={doctorInfoForm.handleSubmit}
                          textContent="Submit"
                          submitted={submitted}
                        />
                      </>
                    )}
                  />
                </div>
              </form>
            </div>
          </div>
          <div id="nav" className="my-4 flex flex-col items-center">
            {submitted && (
              <div className="flex flex-col items-center">
                <button
                  id="submit-form-info"
                  title="Submit Form Info"
                  onClick={handleNextButtonClick}
                  disabled={false}
                  className={classNames.nextButton}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
